import React, { useState, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { secureGet } from "../config/api";
import { useTranslation } from "react-i18next";

import DynamicForm from "./DynamicForm";
import ProductForm from "../modules/Products/ProductForm";
import ClientForm from "../modules/Clients/ClientForm";
import PurchaseForm from "../modules/Purchases/PurchaseForm";
import RequestForm from "../modules/Repo/RequestForm";
import ExpenseForm from "../modules/Performance/ExpenseForm";
import QuoteForm from "../modules/Quotes/QuoteForm";
import imageIcon from "../icons/image.svg";
import SnackbarNotification from './SnackbarNotification';

function ModuleWrapper({ moduleType, mode = "add", onClose, onSuccess, initialValues: propInitialValues, title }) {
  const history = useHistory();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const abortController = useMemo(() => new AbortController(), []);
  const signal = abortController.signal;
  const getStoredValues = (key) => {
    const storedData = localStorage.getItem(key);
    return storedData ? JSON.parse(storedData) : {};
  };



  const [initialValues, setInitialValues] = useState(() => {
    if (moduleType === "settings") {
      return getStoredValues(moduleType);
    } else if (moduleType === "profile") {
      return getStoredValues("user");
    }
    // Prioritize propInitialValues (explicitly passed) over location.state
    // This ensures nested forms (like vehicle edit from client form) work correctly
    return propInitialValues || location.state?.initialValues || {};
  });

  const getFormTitle = () => {
    return t(`form_titles.${mode === "modify" ? `edit_${moduleType}` : `add_${moduleType}`}`);
  };

  const [, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const [dropdownOptions, setDropdownOptions] = useState({
    clients: [],
    repos: [],
    parentId: [],
    providers: [],
    products: [],
    services: [],
    families: [],
    brands: [],
    roles: [],
    cities: [],
    banks: [],
    expense_category: [],
    brands: [],
    technicians: [],
    clientType: [],
    communicationChannel: [],
    paymentMode: [],
    fuelType: [],
    vehicleType: [],
    vehicleBrands: [],
    familyType: [],
    documentType: [],
    type: [],
    paymentTypes: [],
  });

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setLoading(true);
        const options = {};

        if (moduleType === "families") {
          const { data: familiesData } = await secureGet("/families", showSnackbar, { signal });

          options.parentId = [
            ...familiesData.map((family) => ({
              value: family.id,
              label: family.name,
            })),
          ];

          options.familyType = [
            { value: "PRODUCT", label: t(`family_types.PRODUCT`) },
            { value: "SERVICE", label: t(`family_types.SERVICE`) },
            { value: "ASSET", label: t(`family_types.ASSET`) },
          ];
        }

        if (moduleType === "purchases") {
          const { data: providersData } = await secureGet("/providers", showSnackbar, { signal });
          options.providers = providersData.map((provider) => ({
            value: provider.id,
            label: provider.name,
          }));

          const { data: reposData } = await secureGet("/repos", showSnackbar, { signal });
          options.repos = reposData.map((repo) => ({
            value: repo.id,
            label: repo.name,
          }));

          const products = await secureGet("/products/unpaginated?familyTypes=PRODUCT&familyTypes=ASSET", showSnackbar, { signal });
          options.products = products.data.map((product) => ({
            value: product.id,
            reference: product.reference,
            name: product.name,
            fullEntity: product,
          }));
        }

        if (moduleType === "quotes") {
          const { data: clientsData } = await secureGet("/clients", showSnackbar, { signal });
          options.clients = clientsData.map((client) => {
            const encoursMaxField = client.encoursMaximum;
            return {
              value: client.id,
              label: client.name,
              reference: client.code,
              name: client.name,
              encoursMax: encoursMaxField ? encoursMaxField.value : null,
            };
          });

          const { data: reposData } = await secureGet("/repos", showSnackbar, { signal });
          options.repos = reposData.map((repo) => ({
            value: repo.id,
            label: repo.name,
          }));

          const products = await secureGet("/products/unpaginated?familyTypes=PRODUCT", showSnackbar, { signal });
          const productsData = products.data?.content || products.data || [];
          options.products = productsData.map((product) => ({
            value: product.id,
            reference: product.reference,
            name: product.name,
            fullEntity: product,
          }));

          const services = await secureGet("/products/unpaginated?familyTypes=SERVICE", showSnackbar, { signal });
          const servicesData = services.data?.content || services.data || [];
          options.services = servicesData.map((service) => ({
            value: service.id,
            reference: service.reference,
            name: service.name,
            fullEntity: service,
          }));

          options.type = [
            { value: "products", label: t("fields.list_pieces") },
            { value: "services", label: t("fields.list_services") },
          ];
        }

        if (moduleType === "products") {
            const currentInitialValues = propInitialValues || location.state?.initialValues || initialValues || {};
            // Check familyType from multiple possible locations
            const familyType = currentInitialValues?.familyType 
              || currentInitialValues?.family?.familyType 
              || initialValues?.familyType 
              || initialValues?.family?.familyType;

            // Fetch families by type if familyType is provided, otherwise fetch all families
            if (familyType) {
            const { data: familiesData } = await secureGet(`/families/type/${familyType}`, showSnackbar, { signal });
            options.families = familiesData.map((family) => ({
              value: family.id,
              label: family.name,
              familyType: family.familyType,
            }));
          } else {
            // If no familyType provided, fetch all families
            const { data: familiesData } = await secureGet("/families", showSnackbar, { signal });
            options.families = familiesData.map((family) => ({
              value: family.id,
              label: family.name,
              familyType: family.familyType,
            }));
          }

          const { data: brandsData } = await secureGet("/brands", showSnackbar, { signal });
          options.brands = brandsData.map((brand) => ({
            value: brand.id,
            label: brand.name,
          }));
        }

        if (moduleType === "requests") {
          const { data: sourceReposData } = await secureGet("/repos", showSnackbar, { signal });
          options.sourceRepos = sourceReposData.map((repo) => ({
            value: repo.id,
            label: repo.name,
          }));

          const { data: destReposData } = await secureGet("/repos", showSnackbar, { signal });
          options.destRepos = destReposData.map((repo) => ({
            value: repo.id,
            label: repo.name,
          }));

          const products = await secureGet("/products/unpaginated?familyTypes=ASSET&familyTypes=PRODUCT", showSnackbar, { signal });
          console.log("products", products);
          options.products = products.data.map((product) => ({
            value: product.id,
            reference: product.reference,
            name: product.name,
            fullEntity: product,
          }));
        }

        if (moduleType === "users") {
          const { data: reposData } = await secureGet("/repos", showSnackbar, { signal });
          options.repos = reposData.map((repo) => ({
            value: repo.id,
            label: repo.name,
            active: repo.active,
          }));

          const roleKeys = Object.keys(t("roles", { returnObjects: true }) || {});
          options.roles = roleKeys.map((roleKey) => ({
            value: roleKey,
            label: t(`roles.${roleKey}`),
          }));
        }

        if (moduleType === "clients") {
          const { data: citiesData } = await secureGet("/cities", showSnackbar, { signal });
          options.cities = citiesData.map((city) => ({
            value: city.id,
            label: city.name,
          }));

          options.clientType = [
            { value: "PRIVATE", label: t(`filters.clients.individual`) },
            { value: "COMPANY", label: t(`filters.clients.company`) },
            { value: "LLD", label: t(`filters.clients.longTermLease`) },
            { value: "INSURANCE", label: t(`filters.clients.insurance`) },
          ];

          options.communicationChannel = [
            { value: "SMS", label: t(`filters.clients.sms`) },
            { value: "PHONE", label: t(`filters.clients.phone`) },
            { value: "EMAIL", label: t(`filters.clients.email`) },
          ];

          options.paymentMode = [
            { value: "CASH", label: t(`paymentModes.CASH`) },
            { value: "CHECK", label: t(`paymentModes.CHECK`) },
            { value: "CARD", label: t(`paymentModes.CARD`) },
            { value: "TRANSFER", label: t(`paymentModes.TRANSFER`) },
            { value: "DEPOSIT", label: t(`paymentModes.DEPOSIT`) },
          ];
        }

        if (moduleType === "expenses") {
          const expenses = Object.keys(t("expense_types", { returnObjects: true }) || {});
          options.expense_category = expenses.map((roleKey) => ({
            value: roleKey,
            label: t(`expense_types.${roleKey}`),
          }));
        }

        if (moduleType === "vehicles") {
          options.fuelType = [
            { value: "GASOLINE", label: t(`filters.vehicles.GASOLINE`) },
            { value: "DIESEL", label: t(`filters.vehicles.DIESEL`) },
            { value: "ELECTRIC", label: t(`filters.vehicles.ELECTRIC`) },
            { value: "HYBRID", label: t(`filters.vehicles.HYBRID`) },
            { value: "LPG", label: t(`filters.vehicles.LPG`) },
            { value: "CNG", label: t(`filters.vehicles.CNG`) },
            { value: "OTHER", label: t(`filters.vehicles.OTHER`) },
          ];
          options.vehicleType = [
            { value: "CAR", label: t(`filters.vehicles.CAR`) },
            { value: "MOTORCYCLE", label: t(`filters.vehicles.MOTORCYCLE`) },
            { value: "TRUCK", label: t(`filters.vehicles.TRUCK`) },
            { value: "VAN", label: t(`filters.vehicles.VAN`) },
            { value: "SUV", label: t(`filters.vehicles.SUV`) },
            { value: "OTHER", label: t(`filters.vehicles.OTHER`) },
          ];

          const { data: vehicleBrandsData } = await secureGet("/vehicle-brands", showSnackbar, { signal });
          options.vehicleBrands = vehicleBrandsData.map((brand) => ({
            value: brand.id,
            label: brand.name,
          }));
        }

        if (moduleType === "services" || moduleType === "materials") {
          const { data: familiesData } = await secureGet("/families", showSnackbar, { signal });
          options.families = familiesData.map((family) => ({
            value: family.id,
            label: family.name,
            familyType: family.familyType, // Include familyType for ProductForm to check visibility
          }));
        }

        if (moduleType === "reception-documents") {
          const { data: clientsData } = await secureGet("/clients", showSnackbar, { signal });
          options.clients = clientsData.map((client) => {
            return {
              value: client.id,
              label: client.name,
              reference: client.code,
              name: client.name,
            };
          });
          options.vehicles = [];
        }

        if (moduleType === "assignTask") {
          const { data: techniciansData } = await secureGet("/users/ROLE_TECHNICIAN", showSnackbar, { signal });
          options.technicians = techniciansData.map((technician) => ({
            value: technician.id,
            label: `${technician.firstName} ${technician.lastName}`,
          }));
        }

        if (moduleType === "documents") {
          options.documentType = [
            { value: "VEHICLE_PICTURE", label: t(`document_types.VEHICLE_PICTURE`) },
            { value: "INSURANCE_DOCUMENT", label: t(`document_types.INSURANCE_DOCUMENT`) },
            { value: "IDENTIFICATION_DOCUMENT", label: t(`document_types.IDENTIFICATION_DOCUMENT`) },
            { value: "SIGNED_RECEPTION_DOCUMENT", label: t(`document_types.SIGNED_RECEPTION_DOCUMENT`) },
            { value: "OTHER", label: t(`document_types.OTHER`) },
          ];
        }

        if (moduleType === "invoice_payment") {
          options.paymentTypes = [
            { value: "CASH", label: t(`paymentModes.CASH`) },
            { value: "CHECK", label: t(`paymentModes.CHECK`) },
            { value: "CARD", label: t(`paymentModes.CARD`) },
            { value: "TRANSFER", label: t(`paymentModes.TRANSFER`) },
            { value: "DEPOSIT", label: t(`paymentModes.DEPOSIT`) },
          ];
        }
        setDropdownOptions((prevOptions) => ({ ...prevOptions, ...options }));
      } catch (error) {
        console.error("Error fetching dropdown options:", error);
      }
    };
    fetchOptions();

    return () => {
      abortController.abort();
    };
  }, [moduleType, signal, t, abortController]);

  const getProductFormFields = () => {
    // Check if familyType is "PRODUCT" to determine if brandId is required
    // brandId is required only when familyType is "PRODUCT"
    const familyType = initialValues?.familyType || propInitialValues?.familyType || "";
    const isProduct = familyType === "PRODUCT";
    const brandRequired = isProduct; // Required only when familyType is "PRODUCT"

    return {
      baseFields: [
        { name: "name", label: t(`fields.products.name`), required: true },
        { name: "reference", label: t(`fields.products.reference`), required: true },
        { name: "priceHT", label: t(`fields.products.priceHT`), type: "text", required: true },
        { name: "priceTTC", label: t(`fields.products.priceTTC`), type: "text", required: true },
        { name: "discount", label: t(`fields.products.discount`), type: "number" },
        {
          name: "familyId",
          label: t(`fields.products.familyId`),
          type: "dropdown",
          options: dropdownOptions.families,
          required: true,
        },
        {
          name: "brandId",
          label: t(`fields.products.brandId`),
          type: "dropdown",
          options: dropdownOptions.brands,
          required: brandRequired, // Required only when familyType is "PRODUCT"
        },
      ],
      fileFields: [
        {
          name: "logo",
          label: t(`fields.products.logo`),
          icon: <img src={imageIcon} alt='product logo' />,
          accept: "image/*",
          placeholderText1: t(`placeholder.upload_logo1`),
          placeholderText2: t(`placeholder.upload_logo2`),
        },
      ],
    };
  };

  const getClientFormFields = () => {
    return {
      baseFields: [
        { name: "name", label: t(`fields.clients.name`), required: true },
        //{ name: "code", label: t(`fields.clients.code`), required: true },
        { name: "cityId", label: t(`fields.clients.cityId`), type: "dropdown", options: dropdownOptions.cities, required: true },
        { name: "address", label: t(`fields.clients.address`) },
        { name: "phone", label: t(`fields.clients.phone`), required: true },
        { name: "email", label: t(`fields.clients.email`), required: true },
        { name: "clientType", label: t(`fields.clients.type`), type: "dropdown", options: dropdownOptions.clientType, required: true },
        { name: "preferredCommunicationChannel", label: t(`fields.clients.communicationChannel`), type: "dropdown", options: dropdownOptions.communicationChannel, required: true },
        { name: "pieceReglementAutorisee", label: t(`fields.clients.paymentMode`), type: "dropdown", options: dropdownOptions.paymentMode, render: (value) => t(`paymentModes.${value}`) },
        { name: "encoursMaximum", label: t(`fields.clients.encoursMaximum`), type: "number", showCondition: (formValues) => formValues.clientType === "COMPANY" },
        { name: "echeanceAutorisee", label: t(`fields.clients.echeanceAutorisee`), type: "number", showCondition: (formValues) => formValues.clientType === "COMPANY" },
      ],
    };
  };

  const getFieldSpecs = () => {
    switch (moduleType) {
      case "brands":
        return [
          { label: t(`fields.brands.name`), name: "name", required: true },
        ];
      case "families":
        return [
          { label: t(`fields.families.name`), name: "name", required: true },
          { label: t(`fields.families.type`), name: "familyType", type: "dropdown", options: dropdownOptions.familyType, required: true },
          { label: t(`fields.families.parentId`), name: "parentId", type: "dropdown", options: dropdownOptions.parentId },

        ];
      case "quotes":
        return [
          { label: t(`fields.orders.clientId`), name: "clientId", type: "text", required: true },
          { label: t(`fields.orders.repoId`), name: "repoId", type: "dropdown", required: true, options: dropdownOptions.repos },
          { label: t(`fields.acompte`), name: "advancePayment", type: "number", required: false },

        ];
      case "purchases":
        return [
          { label: t(`fields.payments.date_facture`), name: "dateFacture", type: "date", required: true },
          { label: t(`fields.purchases.blNumber`), name: "factureNumber", required: true },
          { label: t(`fields.purchases.fournisseur`), name: "fournisseur", type: "dropdown", options: dropdownOptions.providers, required: true },
          { label: t(`fields.purchases.stockAlimenter`), name: "stockAlimenter", type: "dropdown", options: dropdownOptions.repos, required: true },
        ];
      case "users":
        return [
          { label: t(`fields.users.firstName`), name: "firstName", required: true },
          { label: t(`fields.users.lastName`), name: "lastName", required: true },
          { label: t(`fields.users.username`), name: "username", required: true },
          { label: t(`fields.users.email`), name: "email", required: true },
          {
            label: t(`fields.users.role`), name: "role", type: "dropdown", required: true,
            options: dropdownOptions.roles,
            conditionalFields: {
              ROLE_TECHNICIAN: [
                { name: "workshopId", label: t('fields.users.techNumber'), type: 'text', required: true },
                //{ name: "repositories", label: t('fields.users.repositories'), type: 'dropdown', options: dropdownOptions.repos },
              ],
            },
          },
          { label: t(`fields.users.password`), name: "password", type: "password" },
          { label: t(`fields.users.passwordConfirmation`), name: "passwordConfirmation", type: "password", required: true, isPasswordConfirmation: true },
        ];
      case "repos":
        return [
          { label: t(`fields.repos.name`), name: "name", required: true },
        ];
      case "providers":
        return [
          { label: t(`fields.providers.name`), name: "name", required: true },
        ];
      case "requests":
        return [
          { label: t(`fields.requests.sourceRepo`), name: "sourceRepo", type: "dropdown", required: true, options: dropdownOptions.sourceRepos },
          { label: t(`fields.requests.destRepo`), name: "destRepo", type: "dropdown", required: true, options: dropdownOptions.destRepos },
        ];
      case "profile":
        return [
          { label: t(`fields.users.firstName`), name: "firstName" },
          { label: t(`fields.users.lastName`), name: "lastName" },

        ];
      case "settings":
        return [
          { label: t(`fields.settings.grayName`), name: "companyName", required: true },
          { label: t(`fields.settings.fname`), name: "contactFirstName", required: true },
          { label: t(`fields.settings.lname`), name: "contactLastName", required: true },
          { label: t(`fields.settings.contactEmail`), name: "contactEmail" },
          { label: t(`fields.settings.phoneNumber`), name: "phoneNumber", required: true },
          {
            label: t(`fields.settings.footer`),
            name: "footer",
            type: "text",
            isNormal: true
          },
        ];
      case "creditNotes":
        return [
          { label: t(`fields.orders.clientId`), name: "client", type: "dropdown", required: true, options: dropdownOptions.clients },
          { label: t(`fields.product`), name: "product", type: "dropdown", required: true, options: dropdownOptions.products },
        ]
      case "expenses":
        return [
          { label: "Notes", name: "comment", type: "textarea", required: false },
        ];
      case "banks":
        return [
          { label: t(`fields.banks.name`), name: "name", type: "text", required: true },
        ];
      case "paymentTypes":
        return [
          { label: "Type du paiement", name: "name", type: "text", required: true },
        ];
      case "vehicles":
        return [
          { label: t(`filters.vehicles.type`), name: "type", type: "dropdown", required: true, options: dropdownOptions.vehicleType },
          { label: t(`filters.vehicles.brand`), name: "vehicleBrandId", type: "dropdown", required: true, options: dropdownOptions.vehicleBrands },
          { label: t(`filters.vehicles.model`), name: "model", type: "text", required: true },
          { label: t(`filters.vehicles.color`), name: "color", type: "text", required: true },
          { label: t(`filters.vehicles.fuelType`), name: "fuelType", type: "dropdown", required: true, options: dropdownOptions.fuelType },
          { label: t(`filters.vehicles.registrationNumber`), name: "immatriculationNumber", type: "text", required: true },
          { label: t(`filters.vehicles.vin`), name: "vinNumber", type: "text", required: true },
        ];
      case "reception-documents":
        return [
          {
            label: t(`fields.ficheReception.clientId`),
            name: "clientId",
            type: "dropdown",
            required: true,
            options: dropdownOptions.clients,
            clearFieldsOnChange: ['vehicleId'],
            onChange: async (value) => {
              if (value) {
                try {
                  const { data: vehiclesData } = await secureGet(`/vehicles/client/${value}`, showSnackbar);
                  console.log("vehiclesData", vehiclesData);
                  const vehicleOptions = vehiclesData.map((vehicle) => ({
                    value: vehicle.id,
                    label: `${t(`filters.vehicles.${vehicle.type}`)} - ${vehicle.brandMake || ''} (${vehicle.immatriculationNumber || ''})`,
                    reference: `${vehicle.immatriculationNumber} - ${vehicle.color || ''}`,
                    name: `${t(`filters.vehicles.${vehicle.type}`)} - ${vehicle.brand?.name || ''}`.trim(),
                  }));
                  setDropdownOptions((prev) => ({ ...prev, vehicles: vehicleOptions }));
                  console.log(`Loaded ${vehicleOptions.length} vehicles for client ${value}`);
                } catch (error) {
                  console.error("Error fetching vehicles for client:", error);
                  setDropdownOptions((prev) => ({ ...prev, vehicles: [] }));
                }
              } else {
                setDropdownOptions((prev) => ({ ...prev, vehicles: [] }));
              }
            }
          },
          { label: t(`fields.ficheReception.vehicleId`), name: "vehicleId", type: "dropdown", required: true, options: dropdownOptions.vehicles },
          { label: t(`fields.ficheReception.km`), name: "kilometers", type: "number", required: true },
          { label: t(`fields.ficheReception.carburant`), name: "fuelLevel", type: "number", required: true, max: 100, min: 0 },
        ];
      case "services":
        return [
          { label: t(`fields.services.name`), name: "name", type: "text", required: true },
          { label: t(`fields.services.reference`), name: "reference", type: "text", required: true },
          { label: t(`fields.services.priceHT`), name: "priceHT", type: "number", required: true },
          { label: t(`fields.services.priceTTC`), name: "priceTTC", type: "number", required: true },
          { label: t(`fields.services.family`), name: "familyId", type: "dropdown", required: true, options: dropdownOptions.families },
        ];
      case "materials":
        return [
          { label: t(`fields.services.name`), name: "name", type: "text", required: true },
          { label: t(`fields.services.reference`), name: "reference", type: "text", required: true },
          { label: t(`fields.services.priceHT`), name: "priceHT", type: "number", required: true },
          { label: t(`fields.services.priceTTC`), name: "priceTTC", type: "number", required: true },
          { label: t(`fields.services.family`), name: "family", type: "dropdown", required: true, options: dropdownOptions.families },
        ];
      case "documents":
        return [
          { label: t(`fields.documents_types`), name: "type", type: "dropdown", required: true, options: dropdownOptions.documentType },
        ];
      case "invoice_payment":
        return [
          { label: "Type de payment", name: "paymentMode", type: "dropdown", required: true, options: dropdownOptions.paymentTypes },
        ];
      case "brandMake":
        return [
          { label: t(`fields.brandMake.name`), name: "name", type: "text", required: true },
        ];
      default:
        return [];
    }
  };
  const getListFieldSpecs = () => {
    if (moduleType === "orders") {
      return [
        { label: t(`fields.list.product`), name: "product", type: "dropdown", required: true, options: dropdownOptions.products },
        { label: t(`fields.list.quantity`), name: "quantity", type: "number", required: true },
      ];
    } else if (moduleType === "quotes") {
      return [
        { label: t(`fields.list_type`), name: "type", type: "dropdown", required: true, options: dropdownOptions.type },
        { label: t(`fields.list.product`), name: "product", type: "dropdown", required: true, options: dropdownOptions.products },
        { label: t(`fields.list.quantity`), name: "quantity", type: "number", required: true },
      ];
    } else if (moduleType === "purchases") {
      return [
        { label: t(`fields.list.product`), name: "productName", type: "dropdown", options: dropdownOptions.products, required: true },
        { label: t(`fields.list.quantity`), name: "quantity", type: "number", required: true },
        { label: t(`fields.list.price`), name: "unitPrice", required: true },
      ];
    } else if (moduleType === "requests") {
      return [
        { label: t(`fields.list.product`), name: "product", type: "dropdown", options: dropdownOptions.products, required: true },
        { label: t(`fields.list.quantity`), name: "quantity", type: "number", required: true },
      ];
    } else if (moduleType === "expenses") {
      return [
        { label: t(`rows.category`), name: "category", type: "dropdown", options: dropdownOptions.expense_category, required: true },
        { label: t(`fields.payments.amount`), name: "price", required: true },
        { label: t(`fields.payments.other`), name: "comment", type: "text", required: false },
      ];
    }
    return [];
  };

  const getRedirectUrl = (familyTypeOverride = null) => {
    // For products module, check familyType to determine redirect URL
    if (moduleType === "products") {
      const familyType = familyTypeOverride || initialValues?.familyType || initialValues?.family?.familyType || "";
      if (familyType === "SERVICE") {
        return "/catalog/services";
      } else if (familyType === "ASSET") {
        return "/catalog/materiels";
      } else {
        return "/catalog/products"; // Default to PRODUCT
      }
    }

    switch (moduleType) {
      case "brands":
        return "/catalog/brands";
      case "families":
        return "/catalog/families";
      case "orders":
        return "/orders";
      case "purchases":
        return "/purchases";
      case "users":
        return "/users";
      case "userobj":
        return "/users";
      case "repos":
        return "/repos";
      case "providers":
        return "/providers";
      case "clients":
        return "/clients";
      case "requests":
        return "/transfers";
      case "profile":
        return "/";
      case "settings":
        return "/";
      case "creditNotes":
        return "/credit-notes";
      case "quotes":
        return `/fiche-reception/details/${initialValues.receptionDocumentId}`;
      case "payments":
        return "/payments";
      case "afournisseur":
        return "/avoirs-fournisseurs";
      case "afinancier":
        return "/avoirs-financiers";
      case "invoices":
        return "/invoices";
      case "expenses":
        return "/expenses";
      case "banks":
        return "/banks";
      case "paymentTypes":
        return "/payment-types";
      case "reception-documents":
        return "/fiche-reception";
      case "documents":
        return `/fiche-reception/details/${initialValues.receptionDocumentId}`;
      case "brandMake":
        return "/vehicle-brands";
      case "invoice_payment":
        return `/invoices/details/${initialValues.billId}`;
      case "vehicles":
        return `/clients/details/${initialValues.clientId}`;
      default:
        return "/";
    }
  };

  const redirectUrl = getRedirectUrl();

  let fields = getFieldSpecs().map((field) => ({
    ...field,
    value: mode === "modify" ? initialValues[field.name] : "",
  }));

  if (moduleType === "vehicles" && mode === "modify") {
    console.log("ModuleWrapper - Vehicle modify mode, initialValues:", initialValues);
    console.log("ModuleWrapper - Fields before mapping:", fields);
  }

  // Filter out password fields in modify mode for users
  if (mode === "modify" && moduleType === "users") {
    fields = fields.filter(field => field.name !== "password" && field.name !== "passwordConfirmation");
  }

  if (mode === "modify" && initialValues.id && moduleType !== "afinancier") {
    fields.push({ name: "id", value: initialValues.id, type: "hidden" });
  }

  // Add clientId as hidden field for vehicles
  if (moduleType === "vehicles" && initialValues.clientId) {
    fields.push({ name: "clientId", value: initialValues.clientId, type: "hidden" });
  }

  // Handle nested brand object for vehicles in modify mode
  if (moduleType === "vehicles" && initialValues.brand) {
    fields = fields.map(field => {
      if (field.name === 'vehicleBrandId') {
        return { ...field, value: initialValues.brand.id };
      }
      return field;
    });
  }

  // Add receptionDocumentId as hidden field for quotes
  if (moduleType === "quotes" && initialValues.receptionDocumentId) {
    fields.push({ name: "receptionDocumentId", value: initialValues.receptionDocumentId, type: "hidden" });
    console.log("receptionDocumentId hidden has been added to fields", initialValues.receptionDocumentId);
  }

  const listFields = getListFieldSpecs().map((field) => ({
    ...field,
    value: mode === "modify" ? initialValues[field.name] : "",
  }));

  let fileFields = [];

  switch (moduleType) {
    case "brands":
      fileFields = [{
        name: "image",
        label: "",
        accept: "image/*",
        icon: <img src="/icons/image.svg" alt="Upload" className="w-8 h-8" />,
        placeholderText1: t("placeholder.upload_logo1"),
        placeholderText2: t("placeholder.upload_logo2")
      }];
      break;
    case "settings":
      fileFields = [
        {
          name: "image",
          label: t("fields.logoImage"),
          accept: "image/*",
          icon: <img src="/icons/image.svg" alt="Upload" className="w-8 h-8" />,
          placeholderText1: t("placeholder.upload_logo1"),
          placeholderText2: t("placeholder.upload_logo2")
        },
        {
          name: "header",
          label: t("fields.headerImage"),
          accept: "image/*",
          icon: <img src="/icons/image.svg" alt="Upload" className="w-8 h-8" />,
          placeholderText1: t("placeholder.upload_header"),
          placeholderText2: t("placeholder.upload_logo2")
        }
      ];
      break;
    case "payments":
      fileFields = [
        {
          name: "image",
          label: t("fields.paymentImage"),
          accept: "image/*",
          placeholderText1: t("placeholder.upload_logo1"),
          placeholderText2: t("placeholder.upload_logo2")
        }
      ];
      break;
    case "documents":
      fileFields = [
        {
          name: "file",
          accept: "*",
          placeholderText1: t("placeholder.upload_document1"),
          placeholderText2: t("placeholder.upload_document2")
        }
      ];
      break;
    case "invoice_payment":
      fileFields = [{
        name: "justificationFileName",
        label: "",
        accept: "image/*",
        icon: <img src="/icons/image.svg" alt="Upload" className="w-8 h-8" />,
        placeholderText1: t("placeholder.upload_logo1"),
        placeholderText2: t("placeholder.upload_logo2")
      }];
      break;
    default:
      fileFields = [];
  }

  const productFields = moduleType === "products" ? getProductFormFields() : null;
  const clientFields = getClientFormFields();

  let apiUrl;
  if (moduleType === "documents") {
    apiUrl = `/reception-documents/${initialValues.receptionDocumentId}/attachments/add`;
  } else if (moduleType === "paymentTypes") {
    apiUrl = `/payments/types/add`;
  } else if (moduleType === "services" || moduleType === "materials") {
    apiUrl = "/products/add";
  } else if (moduleType === "brandMake") {
    apiUrl = "/vehicle-brands";
  } else if (moduleType === "invoice_payment") {
    apiUrl = `/bills/${initialValues.billId}/mark-as-paid`;
  } else if (moduleType === "vehicles" && mode === "modify" && initialValues.id) {
    apiUrl = `/vehicles/${initialValues.id}`;
  } else {
    apiUrl = `${moduleType}/add`;
  }

  const handleFormSubmit = async (responseData) => {
    console.log(
      `${mode === "modify" ? "Modifying" : "Adding"} ${moduleType} successful:`,
      responseData
    );

    if (onClose) {
      onClose("submit");
    } else {
      history.push(redirectUrl);
    }
  };

  const handleSuccess = (familyTypeFromForm = null) => {
    const message = mode === 'modify'
      ? t(`confirmation.${moduleType}Edit`)
      : t(`confirmation.${moduleType}Add`);
    if (onSuccess) onSuccess(message);
    
    // For products module, dynamically determine redirect URL based on familyType
    // Priority: familyTypeFromForm (from ProductForm) > initialValues.familyType > initialValues.family?.familyType
    const finalRedirectUrl = moduleType === "products" 
      ? getRedirectUrl(familyTypeFromForm || initialValues?.familyType || initialValues?.family?.familyType)
      : redirectUrl;
    
    history.push({
      pathname: finalRedirectUrl,
      state: {
        showSnackbar: true,
        snackbarMessage: message,
        snackbarSeverity: 'success'
      }
    });

    if (onClose) {
      onClose("submit");
    } else {
      history.push(finalRedirectUrl);
    }
  };

  const handleCancel = () => {

    if (onClose) {
      onClose("cancel");
    } else {
      history.push(redirectUrl);
    }
  };

  const renderPopup = () => {


    const formTitle = getFormTitle();
    if (moduleType === "brands" || moduleType === "families" || moduleType === "users" || moduleType === "repos" || moduleType === "providers" || moduleType === "userobj" || moduleType === "profile" || moduleType === "template" || moduleType === "settings" || moduleType === "banks" || moduleType === "paymentTypes" ||
      moduleType === "vehicles" || moduleType === "reception-documents" || moduleType === "documents" || moduleType === "invoice_payment" || moduleType === "assignTask" || moduleType === "brandMake") {
      return (
        <DynamicForm
          title={formTitle}
          initialValues={initialValues}
          baseFields={fields}
          listFields={listFields}
          products={dropdownOptions.products}
          fileFields={fileFields}
          onSubmit={handleFormSubmit}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
          apiUrl={apiUrl}
          module={moduleType}

        />
      )
    } else if (moduleType === "products" ) {
      // Extract familyType from multiple possible locations
      const familyType = initialValues?.familyType || initialValues?.family?.familyType || "";
      // Use formTitle (which is correctly calculated based on mode) or fallback to title prop if provided
      const productFormTitle = formTitle || title;
      return (
        <ProductForm
          title={productFormTitle}
          initialValues={initialValues}
          baseFields={productFields.baseFields}
          fileFields={productFields.fileFields}
          onSubmit={handleFormSubmit}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
          apiUrl={apiUrl}
        />
      );
    } else if (moduleType === "quotes") {
      return (
        <QuoteForm
          title={formTitle}
          baseFields={fields}
          listFields={listFields}
          products={dropdownOptions.products}
          services={dropdownOptions.services}
          typeOptions={dropdownOptions.type}
          initialValues={initialValues}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      );
    }
  };

  const renderForm = () => {

    const formTitle = getFormTitle();


    if (moduleType === "clients") {
      return (
        <ClientForm
          title={formTitle}
          initialValues={initialValues}
          baseFields={clientFields.baseFields}
          paymentMode={dropdownOptions.paymentMode}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      );
    } else if (moduleType === "purchases") {
      return (
        <PurchaseForm
          title={formTitle}
          baseFields={fields}
          listFields={listFields}
          products={dropdownOptions.products}
          onSubmit={handleFormSubmit}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      )
    } else if (moduleType === "requests") {
      return (
        <RequestForm
          title={formTitle}
          initialValues={initialValues}
          baseFields={fields}
          listFields={listFields}
          products={dropdownOptions.products}
          onSuccess={handleSuccess}
        />
      );
    } else if (moduleType === 'expenses') {
      return (
        <ExpenseForm
          title={formTitle}
          baseFields={fields}
          listFields={listFields}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      );
    } 
  };


  return (
    <div>
      {renderPopup()}
      <div>{renderForm()}</div>
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );

}

export default ModuleWrapper;
